"use client"

import { createContext, useCallback, useContext, useMemo } from 'react'
import type { Locale } from '@/lib/i18n-config'
import type { Dictionary } from '@/app/[lang]/dictionaries'

type NestedKeyOf<O> = O extends object
  ? { [K in keyof O]: K extends string ? (O[K] extends object ? `${K}` | `${K}.${NestedKeyOf<O[K]>}` : K) : never }[keyof O] extends infer R
    ? R extends string
      ? R
      : never
    : never
  : never

type DictKey = NestedKeyOf<Dictionary>

function getNested(obj: Record<string, unknown>, path: string): string | undefined {
  const keys = path.split('.')
  let current: unknown = obj
  for (const key of keys) {
    if (current == null || typeof current !== 'object') return undefined
    current = (current as Record<string, unknown>)[key]
  }
  return typeof current === 'string' ? current : undefined
}

interface I18nContextValue {
  dict: Dictionary
  locale: Locale
  t: (key: DictKey) => string
}

const I18nContext = createContext<I18nContextValue | null>(null)

const defaultLocale: Locale = 'ko'
const fallbackT: (key: DictKey) => string = (key) => String(key)
const fallbackValue: I18nContextValue = {
  dict: {} as Dictionary,
  locale: defaultLocale,
  t: fallbackT,
}

interface I18nProviderProps {
  dictionary: Dictionary
  locale: Locale
  children: React.ReactNode
}

export function I18nProvider({ dictionary, locale, children }: I18nProviderProps) {
  const t = useCallback(
    (key: DictKey) => getNested(dictionary as Record<string, unknown>, key) ?? key,
    [dictionary]
  )
  const value = useMemo(() => ({ dict: dictionary, locale, t }), [dictionary, locale, t])
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext)
  if (!ctx) {
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
      console.warn('useI18n was called outside I18nProvider. Using fallback (locale: ko, t returns key).')
    }
    return fallbackValue
  }
  return ctx
}
